/**
 * Created by Ning Jiang on 11/8/2017.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {WildNudgeGameConfig} from "../WildNudgeGameConfig";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {WildNudgeParsedGameData} from "../server/data/WildNudgeParsedGameData";
import {WildNudgeParsedInitData} from "../server/data/WildNudgeParsedInitData";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SlidingNumberView, SlidingNumberViewData} from "../multiplier/SlidingNumberView";
import {WildNudgeFreespinOutroController} from "../screen/freespinoutro/WildNudgeFreespinOutroController";
import {TimelineLite, TweenLite} from "gsap";

export class FreespinsLeftIndicator extends BaseView {

    public static readonly UPDATE_LEFT_NUMBER:string = "FreespinsLeftEvent_UpdateLeftNumber";
    public static readonly ACTIVATE_INDICATOR:string = "FreespinsLeftEvent_ActivateIndicator";
    public static readonly INCREASE_COMPLETED:string = "Increase_Completed";
    public static readonly FS_ANTICIPATION_LIGHTS_ON:string = "FSAnticipationLightsOn";
    public static readonly FS_LEFT_INDICATOR_BLINK_LIGHTS:string = "FSLeftIndicatorBlinkLights";
    public static readonly FS_LEFT_INDICATOR_STOP_BLINKING:string = "FSLeftIndicatorStopBlinking";

    private _currentCounter:number;
    private _endCounter:number;
    private _layer:PIXI.Container;
    private _fsLidsLayer:PIXI.Container;
    private _fsStripeObject:SlidingNumberView;
    private _fsLeft:number = 0;
    private _upperLid:PIXI.Sprite;
    private _bottomLid:PIXI.Sprite;
    private _data:WildNudgeParsedGameData | WildNudgeParsedInitData;
    private _lights:PIXI.Sprite[];
    private _lightPositions:number[][] = [[212, -16], [212, 36], [460, -16], [458, 36]];
    private _lightsOn:boolean = false;
    private _nudge:boolean = false;
    private _firstNudged:boolean = false;
    private _isLastNudge:boolean = false;
    private _totalFS:number = 0;
    private _isBlinking:boolean = false;

    constructor() {
        super();
        this._currentCounter = 0;

        this._layer = StageManager.getLayer(WildNudgeGameConfig.instance.LAYERS.FREESPINS_LEFT.name);
        this._fsLidsLayer = StageManager.getLayer(WildNudgeGameConfig.instance.LAYERS.FREESPINS_LIDS.name);

        let nVDataObject:SlidingNumberViewData = {
            imageName : "numbersFS",
            digits : 3,
            value : 0,
            imageTopMargin : -1,
            imageHeight: 625,
            digitPadding: 3,
            animationDuration : 0.1,
        };

        this._fsStripeObject = new SlidingNumberView(nVDataObject);
    }

    protected addEventListeners():void {
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event:GameEvent) => this.onDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event:GameEvent) => this.onDataParsed(event.params[0]));

        EventHandler.addEventListener(this, FreespinsLeftIndicator.UPDATE_LEFT_NUMBER, (event:GameEvent) => this.onUpdate(event.params[0]));
        EventHandler.addEventListener(this, FreespinsLeftIndicator.ACTIVATE_INDICATOR, (event:GameEvent) => this.activateIndicator());
        EventHandler.addEventListener(this, FreespinsLeftIndicator.FS_ANTICIPATION_LIGHTS_ON, (event:GameEvent) => this.turnLights(true));
        EventHandler.addEventListener(this, FreespinsLeftIndicator.FS_LEFT_INDICATOR_BLINK_LIGHTS, (event:GameEvent) => {
            if(this._isBlinking) {
                return;
            }

            this._isBlinking = true;
            this.blinkLights();
        });
        EventHandler.addEventListener(this, FreespinsLeftIndicator.FS_LEFT_INDICATOR_STOP_BLINKING, (event:GameEvent) => {this._isBlinking = false});
        EventHandler.addEventListener(this, WildNudgeFreespinOutroController.CLOSE_COMPLETED, (event:GameEvent) => {
            this.closelids();
            this.turnLights(false);
        });
    }

    private onDataParsed(data:WildNudgeParsedGameData | WildNudgeParsedInitData):void {
        this._endCounter = data.freespinTriggeredThisSpin ? data.freespinsLeft! : (data.addedNumberOfFreespinsThisSpin != null ? data.addedNumberOfFreespinsThisSpin : 0);

        this._data = data;

        let doReelNudge:boolean[] = (<WildNudgeParsedGameData>data).doScatterNudge ? (<WildNudgeParsedGameData>data).doScatterNudge : [false, false, false, false, false];

        this._nudge = false;

        for(let i:number = 0; i < 5; i++){
            if(doReelNudge[i]){
                this._nudge = true;
                break;
            }
        }

        this._firstNudged = false;

        if(!this._nudge){
            this._fsLeft = data.freespinsLeft ? data.freespinsLeft : 0;
            this.updateText();
        }
        else if(!this._firstNudged) {
            this._fsLeft--;
            this.updateText();
            this._firstNudged = true;
        }

        this._totalFS = this._data.freespinsLeft ? this._data.freespinsLeft : 0;
    }

    protected initAnimations():void {
        this._lights = [];
        for(let i:number = 0; i < 4; i++){
            this._lights.push(new PIXI.Sprite(GameResources.getTextures("lightBulb")[0]));
            this._lights[i].blendMode = PIXI.BLEND_MODES.ADD;
            this._lights[i].position.set(this._lightPositions[i][0], this._lightPositions[i][1]);
            this._lights[i].alpha = 0;
            this._fsLidsLayer.addChild(this._lights[i]);
        }

        const background:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("freespinPanelBg")[0]);
        this.addChild(background);

        this.updateText();

        this._fsStripeObject.container.position.set(150, 0);
        this.addChild(this._fsStripeObject.container);

        let mask:PIXI.Graphics = new PIXI.Graphics();
        mask.beginFill(0x000000);
        mask.drawRect(0, 0, 255, 54);
        mask.endFill();
        this.addChild(mask);

        this._upperLid = new PIXI.Sprite(GameResources.getTextures("FSIndicatorUpperLid")[0]);
        this._upperLid.mask = mask;

        this.addChild(this._upperLid);

        this._bottomLid = new PIXI.Sprite(GameResources.getTextures("FSIndicatorBottomLid")[0]);
        this._bottomLid.position.set(0, 27);
        this._bottomLid.mask = mask;

        this.addChild(this._bottomLid);

        this.position.set(252, 14);
        this._layer.addChild(this);
    }

    private activateIndicator():void{
        const timer:TimelineLite = new TimelineLite();
        timer.add(() => {
            this._isBlinking = false;
            this.openLids();
            this.updateText();
        }, 0.1);
        timer.add(() => {
            this.turnLights(true);
        }, 0.7);
    }

    private openLids():void{
        TweenLite.fromTo(this._upperLid, 0.2, {y: 0}, {y: -27});
        TweenLite.fromTo(this._bottomLid, 0.2, {y: 27}, {y: 54});
        if(typeof this._data.freespinsLeft === "number"){
            if(this._data.addedNumberOfFreespinsThisSpin)
                this._fsLeft = this._data.freespinsLeft - this._data.addedNumberOfFreespinsThisSpin;
            this.updateText();
        }
    }

    private closelids():void{
        TweenLite.fromTo(this._upperLid, 0.5, {y: -27}, {y: -0});
        TweenLite.fromTo(this._bottomLid, 0.5, {y: 54}, {y: 27});
    }

    private updateText():void {
        const timer:TimelineLite = new TimelineLite();
        timer.add(() => {
            if(!this._isLastNudge)
                this._fsStripeObject.setNumber(this._fsLeft);
            else
                this._fsStripeObject.setNumber(this._fsLeft, ()=>this.finishPresentation());
        }, 0);
    }

    private finishPresentation():void{
        EventHandler.dispatchEvent(new GameEvent(FreespinsLeftIndicator.INCREASE_COMPLETED))
    }

    private onUpdate(number?:number):void {
        this._isLastNudge = false;
        const timer:TimelineLite = new TimelineLite();
        if(number == null) {
            this._currentCounter++;
            this._fsLeft++;
            this.turnLights(false);
            timer.add(() => {
                this.turnLights(true);
            }, 0.2);

        } else {
            this._currentCounter = number;
        }
        if(this._currentCounter > this._endCounter) {
            throw  new Error(`FreespinsLeftIndicator.onUpdate():current counter cannot be bigger than addedNumberOfFreespinsThisSpin!`);
        }else if(this._fsLeft === this._totalFS)
            this._isLastNudge = true;

        this.updateText();
    }

    private turnLights(isOn:boolean, duration?:number, onCompleteCallback?:() => void):void {
        if(this._lightsOn === !isOn) {
            this._lights.forEach(light =>{
                TweenLite.fromTo(light, duration ? duration : 0.5, {alpha : !isOn ? 1 : 0}, {
                    alpha : isOn ? 1 : 0,
                    onComplete : () => {
                        if(onCompleteCallback)
                            onCompleteCallback();
                    }
                });
            });
            this._lightsOn = isOn;
        }
    }

    private blinkLights():void {
        if(this._isBlinking) {
            this.turnLights(
                true, 0.5, () => {this.turnLights(false, 0.2, () => this.blinkLights())}
            )
        }
    }
}